import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { getDeviceFingerprint } from "@/services/deviceFingerprint";

// Pages
import HomePage from "@/pages/HomePage";
import MyCouponsPage from "@/pages/MyCouponsPage";
import StaffPage from "@/pages/StaffPage";
import LandingPage from "@/pages/LandingPage";
import MemberPage from "@/pages/MemberPage";
import AdminDashboard from "@/pages/AdminDashboard";
import InvestOverviewPage from "@/pages/InvestOverviewPage";
import InvestmentDetailPage from "@/pages/InvestmentDetailPage";
import InvestmentCheckoutPage from "@/pages/InvestmentCheckoutPage";
import InvestorDashboard from "@/pages/InvestorDashboard";
import MembershipPage from "@/pages/MembershipPage";
import MembershipCheckoutPage from "@/pages/MembershipCheckoutPage";
import AboPage from "@/pages/AboPage";
import ClaimPage from "@/pages/ClaimPage";
import MemberProfilePage from "@/pages/MemberProfilePage";
import OnlineOrderPage from "@/pages/OnlineOrderPage";
import BackofficePage from "@/pages/BackofficePage";
import ReferralPage from "@/pages/ReferralPage";
import KitchenPage from "@/pages/KitchenPage";
import DriverPage from "@/pages/DriverPage";
import LoyaltyV2Page from "@/pages/LoyaltyV2Page";
import StyleCompare from "@/pages/StyleCompare";
import AuthModal from "@/components/AuthModal";
import BottomNav from "@/components/BottomNav";
import WelcomeRewardModal from "@/components/WelcomeRewardModal";
import LocationSelector from "@/components/LocationSelector";
import ProfileButton from "@/components/ProfileButton";
import InstallPWABanner from "@/components/InstallPWABanner";
import PromoModal from "@/components/PromoModal";
import { LayoutProvider, useLayout } from "@/context/LayoutContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function AppContent() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [welcomeCoupon, setWelcomeCoupon] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(
    localStorage.getItem("frankysfarm_location") || null
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const initApp = async () => {
      // Seed data only once
      if (!localStorage.getItem('frankysfarm_seeded')) {
        try {
          axios.post(`${API}/seed`).catch(() => {});
          localStorage.setItem('frankysfarm_seeded', 'true');
        } catch (e) {}
      }

      // Check if user is logged in
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // If user has a location, use it
          if (response.data.location && !selectedLocation) {
            setSelectedLocation(response.data.location);
            localStorage.setItem("frankysfarm_location", response.data.location);
          }
        } catch (e) {
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };

    initApp();
  }, [token]);

  // Event listener für show-auth-modal Event
  useEffect(() => {
    const handleShowAuthModal = () => {
      setShowAuthModal(true);
    };
    document.addEventListener('show-auth-modal', handleShowAuthModal);
    return () => {
      document.removeEventListener('show-auth-modal', handleShowAuthModal);
    };
  }, []);


  // Promo link detection (?promo=matcha-gratis)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promo = params.get("promo");
    if (promo === "matcha-gratis" && !user) {
      setTimeout(() => setShowPromoModal(true), 800);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [user]);

  // Auto-set default location if not selected (skip location selector)
  useEffect(() => {
    if (!selectedLocation) {
      const defaultLocation = "aachen";
      setSelectedLocation(defaultLocation);
      localStorage.setItem("frankysfarm_location", defaultLocation);
    }
  }, [selectedLocation]);

  const handleLocationSelect = (loc) => {
    setSelectedLocation(loc);
    localStorage.setItem("frankysfarm_location", loc);
    navigate("/landing");
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    // Don't close modal here - let AuthModal handle it based on verification status
    if (userData.verified !== false) {
      setShowAuthModal(false);
      toast.success("Erfolgreich angemeldet!");
    }
    return userData;
  };

  const register = async (formDataOrName, email, phone, password) => {
    // Support both object and individual parameters
    let name, userEmail, userPhone, userPassword, googleReview;
    
    if (typeof formDataOrName === 'object') {
      // Called with formData object
      name = formDataOrName.name;
      userEmail = formDataOrName.email;
      userPhone = formDataOrName.phone;
      userPassword = formDataOrName.password;
      googleReview = formDataOrName.google_review || false;
    } else {
      // Called with individual parameters
      name = formDataOrName;
      userEmail = email;
      userPhone = phone;
      userPassword = password;
      googleReview = false;
    }
    
    // Get device fingerprint to prevent multiple accounts per device
    const deviceId = await getDeviceFingerprint();
    
    const response = await axios.post(`${API}/auth/register`, { 
      name, 
      email: userEmail, 
      phone: userPhone, 
      password: userPassword,
      location: selectedLocation || "aachen",
      device_id: deviceId,
      google_review: googleReview
    });
    const { access_token, user: userData, welcome_coupon } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    setShowAuthModal(false);
    
    // Show welcome modal with confetti if new user gets welcome coupon
    if (welcome_coupon) {
      setWelcomeCoupon(welcome_coupon);
      setShowWelcomeModal(true);
    } else {
      toast.success("Willkommen bei Franky's Farm!");
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    toast.success("Abgemeldet");
    navigate("/landing");
  };

  const authValue = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    showAuthModal,
    setShowAuthModal,
    isAuthenticated: !!user,
    selectedLocation,
    setSelectedLocation: handleLocationSelect
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#8ECFC0' }}>
        <div className="w-8 h-8 border-2 border-[#2D5A3D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Reset location if on /reset path
  if (location.pathname === "/reset") {
    localStorage.removeItem("frankysfarm_location");
    localStorage.removeItem("frankysfarm_visited");
    window.location.href = "/";
    return null;
  }

  const isStaffPage = location.pathname === "/staff";
  const isAdminPage = location.pathname === "/admin";
  const isLandingPage = location.pathname === "/landing" || location.pathname === "/";
  const isHomePage = location.pathname === "/home";
  const isInvestPage = location.pathname.startsWith("/invest");
  const isMembershipPage = location.pathname.startsWith("/membership");
  const isAboPage = location.pathname.startsWith("/abo");
  const isClaimPage = location.pathname.startsWith("/claim/");
  const isProfilePage = location.pathname === "/profile";
  const isOrderPage = location.pathname === "/order";
  const showNav = !isStaffPage && !isAdminPage && !isLandingPage && !isHomePage && !isInvestPage && !isMembershipPage && !isProfilePage && !isOrderPage && !isAboPage && !isClaimPage;
  // Profile button should NOT show on landing page (it has its own logo header)
  // Auf /abo + /claim bewusst ausblenden — fokussierte Single-Action-Seiten.
  const showProfileButton = !isStaffPage && !isAdminPage && !isProfilePage && !isLandingPage && !isOrderPage && !isAboPage && !isClaimPage;

  return (
    <AuthContext.Provider value={authValue}>
      <div className="min-h-screen" style={{backgroundColor:"#F5E6E0"}}>
        <Routes>
          <Route path="/" element={<LandingPage location={selectedLocation} />} />
          <Route path="/landing" element={<LandingPage location={selectedLocation} />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/my-coupons" element={<MyCouponsPage />} />
          <Route path="/member" element={<MemberPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/invest" element={<InvestOverviewPage />} />
          <Route path="/invest/city/:cityId" element={<InvestmentDetailPage />} />
          <Route path="/invest/checkout/:cityId" element={<InvestmentCheckoutPage />} />
          <Route path="/invest/dashboard" element={<InvestorDashboard />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/membership/checkout/:planId" element={<MembershipCheckoutPage />} />
          <Route path="/membership/success" element={<MembershipCheckoutPage />} />
          <Route path="/abo" element={<AboPage />} />
          <Route path="/claim/:week" element={<ClaimPage />} />
          <Route path="/profile" element={<MemberProfilePage />} />
          <Route path="/order" element={<OnlineOrderPage />} />
          <Route path="/backoffice" element={<BackofficePage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/kitchen" element={<KitchenPage />} />
          <Route path="/driver" element={<DriverPage />} />
          <Route path="/v2" element={<LoyaltyV2Page />} />
          <Route path="/compare" element={<StyleCompare />} />
        </Routes>
        
        {showProfileButton && <ProfileButton />}
        
        {showNav && <BottomNav />}
        
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
        
        <WelcomeRewardModal
          isOpen={showWelcomeModal}
          onClose={() => setShowWelcomeModal(false)}
          coupon={welcomeCoupon}
        />
        
        {/* PWA Install Banner – deaktiviert (noch keine App, Stand 2026-06-03) */}
        {/* <InstallPWABanner /> */}
        <PromoModal isOpen={showPromoModal} onClose={() => setShowPromoModal(false)} />
        
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#1a1a1a',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)'
            }
          }}
        />
      </div>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <LayoutProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LayoutProvider>
  );
}

export default App;
